import { useEffect } from "react";
import { isEqual } from "lodash";
import { onFeatureUsage } from "@/utils/gtm";
import useTabStore from "@/stores/useTabStore";
import { useOnboardingStore } from "../useOnboardingStore";

/**
 * Passive checklist completion detection. Subscribes to the app's existing
 * instrumentation instead of sprinkling call sites:
 *
 * - popout_widget      ← widget/popout (WidgetContainer header action)
 * - listen_squawk      ← squawk_player/channel_select (SquawkPlayerContext)
 * - create_watchlist   ← watchlist/create (PortfolioBuilder save)
 * - set_notification   ← notifications settings change events
 * - apply_filter       ← tab store widgetFilters diff (covers filter panels
 *                        in every widget without touching them)
 *
 * markTaskDone is idempotent and no-ops when onboarding never started, so
 * this hook is safe to run for everyone the gate mounts it for.
 */
export function useChecklistDetection(enabled: boolean) {
  const markTaskDone = useOnboardingStore((s) => s.markTaskDone);

  useEffect(() => {
    if (!enabled) return;

    const unsubscribeFeatureUsage = onFeatureUsage(
      ({ feature_name, feature_action }) => {
        if (feature_name === "widget" && feature_action === "popout") {
          markTaskDone("popout_widget");
        }
        if (
          feature_name === "squawk_player" &&
          feature_action === "channel_select"
        ) {
          markTaskDone("listen_squawk");
        }
        if (feature_name === "watchlist" && feature_action === "create") {
          markTaskDone("create_watchlist");
        }
        if (feature_name === "notifications") {
          markTaskDone("set_notification");
        }
      }
    );

    // Any widgetFilters change on any tab = the user drove a filter panel.
    const unsubscribeTabStore = useTabStore.subscribe((state, prevState) => {
      if (!state.tabsLoaded || !prevState.tabsLoaded) return;
      const changed = state.tabs.some((tab) => {
        const prevTab = prevState.tabs.find((t) => t.id === tab.id);
        return prevTab && !isEqual(tab.widgetFilters, prevTab.widgetFilters);
      });
      if (changed) markTaskDone("apply_filter");
    });

    return () => {
      unsubscribeFeatureUsage();
      unsubscribeTabStore();
    };
  }, [enabled, markTaskDone]);
}
